import EventBus from './EventBus';

export const MISSION_EVENTS = {
  UPDATED: 'updated mission',
  CREATED_OR_DELETED: 'created or deleted mission',
  BOUNDARY_UPDATED: 'boundary updated',
  HISTORICAL_MAP_LAYERS_LOADED: 'historical map years loaded',
  DEFECTIVE_GEOMETRIES_UPDATED: 'defective geometries updated',
  DEBUG_GEOJSON_UPDATED: 'debug geojson updated',
};

/**
 * Dispatches mission updated event after ensuring database operations have completed.
 * The setTimeout(0) yields to the event loop, allowing any pending database operations
 * (like dispose(), create(), update()) to complete before the UI refreshes.
 * This prevents race conditions where UI components render with stale data.
 * It works for non-awaited calls - The function returns a Promise that doesn't need to be awaited:
 * The setTimeout(0) will still execute before the EventBus dispatch happens, which is what we want.
 *
 * Does NOT fire `CORES_NEED_UPDATE`. If a call site also changes
 * cores (added/removed cores, moved cores, changed pulled state,
 * changed bag_id, changed waypoint_number, etc.), it must
 * additionally call `dispatchCoresNeedUpdate()` explicitly.
 * `CorePointsLayer` does not listen to `MISSION_EVENTS.UPDATED` —
 * a full 1000-core rebuild on every mission update is a big perf
 * hit on large fields. See Step 13 of
 * docs/superpowers/plans/2026-04-08-kml-layer-split.md.
 */
export async function dispatchMissionUpdated() {
  // Yield to event loop to allow database operations to complete
  await new Promise((resolve) => setTimeout(resolve, 0));

  EventBus.dispatch(MISSION_EVENTS.UPDATED);
}

export type CreatedOrDeletedParams =
  | {
      deletedInstanceId: number | undefined;
      deletedRogoJobId: string | undefined;
    }
  | undefined;

export async function dispatchMissionCreatedDeleted(missionData: CreatedOrDeletedParams = undefined) {
  // Yield to event loop to allow database operations to complete
  await new Promise((resolve) => setTimeout(resolve, 0));

  EventBus.dispatch(MISSION_EVENTS.CREATED_OR_DELETED, missionData);
}

export async function dispatchMissionBoundaryUpdated() {
  // Yield to event loop to allow database operations to complete
  await new Promise((resolve) => setTimeout(resolve, 0));

  EventBus.dispatch(MISSION_EVENTS.BOUNDARY_UPDATED);
}

export async function dispatchMissionHistoricalMapLayersLoaded() {
  // Yield to event loop to allow database operations to complete
  await new Promise((resolve) => setTimeout(resolve, 0));

  EventBus.dispatch(MISSION_EVENTS.HISTORICAL_MAP_LAYERS_LOADED);
}
